import React, { useState, useEffect } from 'react';
import { FeedbackService } from '../services/feedbackService';

const FeedbackModal = ({ 
    isOpen, 
    onClose, 
    showNotification,
    userAccount = null,
    chainId = null,
    walletType = null
}) => {
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [walletAddress, setWalletAddress] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);

    // Sync wallet address with userAccount prop when modal opens or account changes
    useEffect(() => {
        if (isOpen && userAccount) {
            setWalletAddress(userAccount);
        } else if (isOpen && !userAccount) {
            setWalletAddress('');
        }
    }, [isOpen, userAccount]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!message.trim()) {
            showNotification('Please enter your feedback', 'error');
            return;
        }

        setIsSubmitting(true);
        setSubmitSuccess(false);

        try {
            // Get browser and device info
            const browserInfo = FeedbackService.getBrowserInfo();
            
            // Use manually entered wallet address if provided, otherwise fall back to userAccount prop
            const addressToSubmit = walletAddress.trim() || userAccount || null;
            
            await FeedbackService.submitFeedback({
                type: 'feedback',
                subject: subject.trim() || 'User Feedback',
                message: message.trim(),
                userAddress: addressToSubmit,
                chainId: chainId,
                walletType: walletType,
                browserInfo: browserInfo,
                url: window.location.href,
            });

            setSubmitSuccess(true);
            showNotification('Thank you! Your feedback has been submitted.', 'success');
            
            // Reset form after a short delay
            setTimeout(() => {
                setSubject('');
                setMessage('');
                setWalletAddress(userAccount || '');
                setSubmitSuccess(false);
                onClose();
            }, 2000);
        } catch (error) {
            console.error('Error submitting feedback:', error);
            const errorMessage = error.message || 'Failed to submit feedback. Please try again.';
            showNotification(errorMessage, 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        if (!isSubmitting) {
            setSubject('');
            setMessage('');
            setWalletAddress(userAccount || '');
            setSubmitSuccess(false);
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal active feedback-modal" onClick={(e) => {
            if (e.target.classList.contains('modal')) {
                handleClose();
            }
        }}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h3 className="feedback-modal-title">Submit a Feedback</h3>
                    <button 
                        className="modal-close" 
                        onClick={handleClose}
                        disabled={isSubmitting}
                    >
                        <i className="fas fa-times"></i>
                    </button>
                </div>

                <div className="modal-body">
                    {submitSuccess ? (
                        <div className="feedback-success">
                            <div className="success-icon">
                                <i className="fas fa-check-circle"></i>
                            </div>
                            <h4>Thank you!</h4>
                            <p>Your feedback has been submitted successfully. We appreciate your input!</p>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label className="form-label" htmlFor="feedback-subject">
                                    Subject
                                </label>
                                <input
                                    type="text"
                                    id="feedback-subject"
                                    className="form-input"
                                    placeholder="e.g., Bug report, Feature request, or General feedback"
                                    value={subject}
                                    onChange={(e) => setSubject(e.target.value)}
                                    disabled={isSubmitting}
                                    maxLength={200}
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label" htmlFor="feedback-wallet-address">
                                    Wallet Address
                                </label>
                                <input
                                    type="text"
                                    id="feedback-wallet-address"
                                    className="form-input"
                                    placeholder="0x..."
                                    value={walletAddress}
                                    onChange={(e) => setWalletAddress(e.target.value)}
                                    disabled={isSubmitting}
                                    maxLength={42}
                                />
                                <small className="form-help-text">
                                    Please provide your wallet address, especially if reporting transaction issues or other wallet-related problems. This helps us investigate and resolve issues more quickly.
                                </small>
                            </div>

                            <div className="form-group">
                                <label className="form-label" htmlFor="feedback-message">
                                    Feedback or Bug Report *
                                </label>
                                <textarea
                                    id="feedback-message"
                                    className="form-textarea"
                                    rows="6"
                                    placeholder="Share your feedback, report a bug, or suggest improvements. For bug reports, please describe what happened, what you were doing, and any error messages you saw."
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    disabled={isSubmitting}
                                    required
                                    maxLength={2000}
                                />
                                <div className="char-count">{message.length}/2000</div>
                            </div>

                            <div className="modal-actions">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={handleClose}
                                    disabled={isSubmitting}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={isSubmitting || !message.trim()}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <span className="spinner"></span>
                                            Submitting...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-paper-plane"></i>
                                            Submit Feedback
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FeedbackModal;
